import React from "react";
import { Box, IconButton, Modal, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ADVOCACY_PLATFORM_LABEL } from "../../../utils/homeFlow";
import { contestantAgeValue } from "../../../utils/contestantProfile";
import { LIST_MEDIA_BG, SITE_BORDER, SITE_PAGE_BG } from "../../../utils/siteListTheme";

function ProfileDetail({ label, value }) {
  if (!value) return null;
  return (
    <Box>
      <Typography
        sx={{
          color: "rgba(255,255,255,0.55)",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          mb: 0.35,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: "rgba(255,255,255,0.88)",
          fontSize: 14,
          lineHeight: 1.55,
          whiteSpace: "pre-wrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function ContestantProfileModal({
  open,
  onClose,
  image,
  name,
  contestant,
}) {
  if (!open) return null;

  const details = contestant || {};

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="contestant-profile-modal"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 1.5, sm: 2.5 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          outline: "none",
          width: "100%",
          maxWidth: 920,
          maxHeight: "90vh",
          overflow: "auto",
          borderRadius: 3,
          bgcolor: SITE_PAGE_BG,
          border: `1px solid ${SITE_BORDER}`,
          boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 2,
            bgcolor: "rgba(0,0,0,0.55)",
            color: "#fff",
            "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            position: "relative",
            width: "100%",
            minHeight: { xs: 240, sm: 360 },
            maxHeight: { xs: 320, sm: 420 },
            bgcolor: LIST_MEDIA_BG,
          }}
        >
          {image ? (
            <Box
              component="img"
              src={image}
              alt={name || "Contestant"}
              sx={{
                width: "100%",
                height: "100%",
                minHeight: { xs: 240, sm: 360 },
                maxHeight: { xs: 320, sm: 420 },
                objectFit: "contain",
                display: "block",
                bgcolor: LIST_MEDIA_BG,
              }}
            />
          ) : (
            <Box
              sx={{
                minHeight: { xs: 240, sm: 360 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(255,255,255,0.45)",
                fontSize: 15,
              }}
            >
              No profile photo available
            </Box>
          )}

          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              px: { xs: 2, sm: 3 },
              py: { xs: 1.8, sm: 2.2 },
              background:
                "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.88) 100%)",
            }}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.65)",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                mb: 0.35,
              }}
            >
              Contestant
            </Typography>
            <Typography
              id="contestant-profile-modal"
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontFamily: "Montserrat, sans-serif",
                fontSize: { xs: 20, sm: 26 },
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              {name || "Contestant"}
            </Typography>
          </Box>
        </Box>

        {(details.bio ||
          details.hometown ||
          details.profession ||
          details.platform_advocacy ||
          details.hobbies ||
          contestantAgeValue(details)) && (
          <Stack spacing={1.5} sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
            <ProfileDetail label="Age" value={contestantAgeValue(details)} />
            <ProfileDetail label="Bio" value={details.bio} />
            <ProfileDetail label="Hometown" value={details.hometown} />
            <ProfileDetail label="Profession" value={details.profession} />
            <ProfileDetail
              label={ADVOCACY_PLATFORM_LABEL}
              value={details.platform_advocacy}
            />
            <ProfileDetail label="Hobbies" value={details.hobbies} />
          </Stack>
        )}
      </Box>
    </Modal>
  );
}
